import {
  MdLocalHospital, MdCalendarToday, MdHotel, MdMedicalServices,
  MdWorkspacePremium, MdLocationOn, MdPictureAsPdf, MdPlayArrow, MdOpenInNew, MdArticle,
} from "react-icons/md";
import {
  FaGlobe, FaFacebookF, FaTwitter, FaInstagram, FaLinkedinIn, FaYoutube,
} from "react-icons/fa";
import { UPLOADS_BASE } from "../../api/hospitalApi";

const HM_GALLERY_PLACEHOLDERS = [
  { id: 1, color: "#e8f4fc", label: "Building" },
  { id: 2, color: "#eef2ff", label: "Facility" },
  { id: 3, color: "#f0fdf4", label: "Campus" },
];

const HM_NEWS_ITEMS = [
  {
    id: 1,
    title: "Times of India Post",
    date: "12/12/2010",
    summary:
      "The Times of India (TOI) is an Indian English-language daily newspaper and digital news media owned and managed by The Times Group. It is the third-largest newspaper in India by circulation and largest-selling English-language daily in the world.",
    link: "#",
  },
];

const getAccreditationLabels = (ids, accreditations) => {
  if (!ids) return "—";
  return ids
    .toString()
    .split(",")
    .map((id) => accreditations.find((a) => String(a.accreditation_id) === id.trim())?.accreditation_title)
    .filter(Boolean)
    .join(", ") || "—";
};

const HospitalProfileView = ({ hospital, accreditations = [] }) => {
  if (!hospital) return null;

  const accrLabel = hospital.accreditation_display
    || getAccreditationLabels(hospital.accreditation_ids, accreditations);
  const addressParts = [hospital.address, hospital.city_name, hospital.state_name, hospital.country_name]
    .filter(Boolean)
    .join(", ");
  const logoUrl = hospital.logo_path ? `${UPLOADS_BASE}/uploads/${hospital.logo_path}` : hospital.logo || null;
  const pdfUrl = hospital.profile_pdf_path
    ? `${UPLOADS_BASE}/uploads/${hospital.profile_pdf_path}`
    : null;
  const showPdfBtn = pdfUrl || hospital.profile_pdf_available;

  const connectLinks = [
    { key: "website", label: "Website", icon: FaGlobe, url: hospital.website_address },
    { key: "facebook", label: "Facebook", icon: FaFacebookF, url: hospital.facebook_page },
    { key: "twitter", label: "Twitter", icon: FaTwitter, url: hospital.x_profile },
    { key: "instagram", label: "Instagram", icon: FaInstagram, url: hospital.instagram },
    { key: "linkedin", label: "LinkedIn", icon: FaLinkedinIn, url: hospital.linkedin },
    { key: "youtube", label: "YouTube", icon: FaYoutube, url: hospital.youtube_channel },
  ].filter((l) => l.url && !String(l.url).includes("/embed/"));

  const stats = [
    { id: "beds", label: "Beds", value: hospital.total_beds ?? "—", icon: MdHotel },
    { id: "doctors", label: "Doctors", value: hospital.total_doctors ?? "—", icon: MdMedicalServices },
    { id: "accr", label: "Accreditation", value: accrLabel, icon: MdWorkspacePremium },
  ];

  const tourSource = hospital.virtual_tour_url || hospital.youtube_channel;
  const videoEmbedUrl = tourSource?.includes("embed")
    ? tourSource
    : tourSource?.replace("watch?v=", "embed/");

  const addressDisplay = hospital.city_name || addressParts || "—";

  return (
    <div className="hm-page">
      <div className="hm-hero">
        <div className="hm-hero-logo">
          {logoUrl ? (
            <img src={logoUrl} alt={`${hospital.establishment_name} logo`} />
          ) : (
            <div className="hm-hero-logo-placeholder">
              <MdLocalHospital size={44} />
              <span className="hm-hero-shield" aria-hidden="true" />
            </div>
          )}
        </div>
        <div className="hm-hero-content">
          <div className="hm-badges">
            {hospital.category && <span className="hm-badge">{hospital.category}</span>}
            {hospital.establishment_year && (
              <span className="hm-badge">
                <MdCalendarToday size={13} /> Est. {hospital.establishment_year}
              </span>
            )}
            {accrLabel !== "—" && (
              <span className="hm-badge">
                <MdWorkspacePremium size={13} /> {accrLabel}
              </span>
            )}
          </div>
          <h1 className="hm-hero-title">{hospital.establishment_name}</h1>
          {hospital.parent_establishment_name && (
            <p className="hm-hero-subtitle">A unit of {hospital.parent_establishment_name}</p>
          )}
          {hospital.short_profile && (
            <p className="hm-hero-desc">{hospital.short_profile}</p>
          )}
        </div>
      </div>

      <div className="hm-stats-row">
        {stats.map(({ id, label, value, icon: Icon }) => (
          <div key={id} className="hm-stat-card">
            <div className="hm-stat-icon">
              <Icon />
            </div>
            <div className="hm-stat-text">
              <span className="hm-stat-label">{label}</span>
              <span className={`hm-stat-value${id === "accr" ? " hm-stat-value--sm" : ""}`}>{value}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="hm-about-grid">
        <div className="hm-card hm-card--about">
          <h3 className="hm-card-title">About</h3>
          <p className="hm-about-text">
            {hospital.long_profile || hospital.short_profile || "No description available."}
          </p>
        </div>
        <div className="hm-sidebar-cards">
          <div className="hm-card hm-card--sidebar">
            <h3 className="hm-card-title">
              <MdLocationOn /> Address
            </h3>
            <p className="hm-card-text">{addressDisplay}</p>
          </div>
          <div className="hm-card hm-card--sidebar">
            <h3 className="hm-card-title">
              <MdPictureAsPdf /> PDF Profile
            </h3>
            {showPdfBtn ? (
              pdfUrl ? (
                <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="hm-download-btn">
                  <MdPictureAsPdf /> Download PDF
                </a>
              ) : (
                <button type="button" className="hm-download-btn" disabled>
                  <MdPictureAsPdf /> Download PDF
                </button>
              )
            ) : (
              <p className="hm-card-text hm-card-text--muted">No PDF profile uploaded.</p>
            )}
          </div>
        </div>
      </div>

      <div className="hm-media-grid">
        <div className="hm-card hm-card--media">
          <h3 className="hm-card-title">
            <MdPlayArrow /> Virtual Tour
          </h3>
          {videoEmbedUrl ? (
            <div className="hm-video-wrap">
              <iframe title="Virtual Tour" src={videoEmbedUrl} allowFullScreen />
            </div>
          ) : (
            <div className="hm-video-placeholder">
              <MdPlayArrow size={40} />
              <span>No virtual tour video available</span>
            </div>
          )}
        </div>

        <div className="hm-card hm-card--media">
          <h3 className="hm-card-title">Connect</h3>
          {connectLinks.length > 0 ? (
            <div className="hm-connect-grid">
              {connectLinks.map(({ key, label, icon: Icon, url }) => (
                <a key={key} href={url} target="_blank" rel="noopener noreferrer" className="hm-connect-btn">
                  <Icon /> {label}
                </a>
              ))}
            </div>
          ) : (
            <p className="hm-card-text hm-card-text--muted">No social links added yet.</p>
          )}
        </div>
      </div>

      <div className="hm-card">
        <h3 className="hm-card-title">Picture Gallery</h3>
        <div className="hm-gallery">
          {logoUrl && (
            <div className="hm-gallery-item">
              <img src={logoUrl} alt="Hospital" />
            </div>
          )}
          {HM_GALLERY_PLACEHOLDERS.map((item) => (
            <div key={item.id} className="hm-gallery-item hm-gallery-item--placeholder" style={{ background: item.color }}>
              <MdLocalHospital size={40} color="#0f66a4" />
            </div>
          ))}
        </div>
      </div>

      <div className="hm-card hm-card--news">
        <h3 className="hm-card-title">
          <MdArticle /> News &amp; Media
        </h3>
        {HM_NEWS_ITEMS.map((item) => (
          <div key={item.id} className="hm-news-item">
            <div className="hm-news-head">
              <div>
                <h4 className="hm-news-title">{item.title}</h4>
                <span className="hm-news-date">{item.date}</span>
              </div>
              <a href={item.link} className="hm-news-link" target="_blank" rel="noopener noreferrer">
                Read <MdOpenInNew size={14} />
              </a>
            </div>
            <p className="hm-news-summary">{item.summary}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HospitalProfileView;
